import React, { FC } from 'react';
import styled from 'styled-components/macro';
import { useFieldArray } from 'react-hook-form';
import * as MESSAGES from 'constants/messages';
import media from 'styles/media';
import theme from 'styles/theme';
import {
  PROFILE_FIELDS_MAX_LENGTH,
  MAX_NEW_GRADUATE_RESEARCH_ROOMS,
} from 'constants/profile';
import {
  ProfileFormControl,
  ProfileFormRegister,
  ProfileFormValues,
} from 'types/profile';
import { TextInput, Textarea, AddField } from 'components/atoms';
import { Field } from 'components/molecules';

const Wrapper = styled.div``;

const FieldsContainer = styled.div<{ isLast: boolean }>`
  display: flex;
  margin-bottom: ${({ isLast }) => (isLast ? 0 : '24px')};
  padding-bottom: ${({ isLast }) => (isLast ? 0 : '16px')};
  border-bottom: ${({ isLast }) =>
    isLast ? 'none' : `1px solid ${theme.borderDefault}`};
`;

const Fields = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const ResearchRoomNameTextInput = styled(TextInput)`
  width: 412px;

  ${media.mobile} {
    width: 100%;
  }
`;

const ResearchRoomNameTextarea = styled(Textarea)`
  textarea {
    min-height: 83px;

    ${media.mobile} {
      min-height: 162px;
    }
  }
`;

const RemoveResearchRoom = styled.div`
  align-self: flex-end;
  margin-top: 6px;
  color: ${theme.basePrimary};
  font-size: 12px;
  line-height: 1.5;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

type Value = {
  newGraduate: Pick<ProfileFormValues['newGraduate'], 'researchRooms'>;
};

type NewGraduateResearchRoomsFormFieldProps = {
  register: ProfileFormRegister<Value>;
  control: ProfileFormControl<Value>;
};

const NewGraduateResearchRoomsFormField: FC<NewGraduateResearchRoomsFormFieldProps> = props => {
  const { register, control } = props;

  const { fields, append, remove } = useFieldArray<
    ProfileFormValues['newGraduate']['researchRooms'][0]
  >({
    control,
    name: 'newGraduate.researchRooms',
  });

  return (
    <Wrapper>
      {fields.map((field, index) => (
        <FieldsContainer
          key={field?.id ?? index}
          isLast={index === MAX_NEW_GRADUATE_RESEARCH_ROOMS - 1}
        >
          <Fields data-testid="new-graduate-research-room-fields">
            <Field
              label={
                MESSAGES.PROFILE.FIELD.NEW_GRADUATE_RESEARCH_ROOMS.NAME.LABEL
              }
            >
              <ResearchRoomNameTextInput
                enterKeyHint="enter"
                name={`newGraduate.researchRooms[${index}].name`}
                maxLength={
                  PROFILE_FIELDS_MAX_LENGTH.NEW_GRADUATE_RESEARCH_ROOM_NAME
                }
                placeholder={
                  MESSAGES.PROFILE.FIELD.NEW_GRADUATE_RESEARCH_ROOMS.NAME
                    .PLACEHOLDER
                }
                aria-label={
                  MESSAGES.PROFILE.FIELD.NEW_GRADUATE_RESEARCH_ROOMS.NAME.LABEL
                }
                ref={register()}
                defaultValue={field.name}
              />
            </Field>

            <Field
              label={
                MESSAGES.PROFILE.FIELD.NEW_GRADUATE_RESEARCH_ROOMS.CONTENT.LABEL
              }
              style={{ marginBottom: 0 }}
            >
              <ResearchRoomNameTextarea
                name={`newGraduate.researchRooms[${index}].content`}
                maxLength={
                  PROFILE_FIELDS_MAX_LENGTH.NEW_GRADUATE_RESEARCH_ROOM_CONTENT
                }
                placeholder={
                  MESSAGES.PROFILE.FIELD.NEW_GRADUATE_RESEARCH_ROOMS.CONTENT
                    .PLACEHOLDER
                }
                aria-label={
                  MESSAGES.PROFILE.FIELD.NEW_GRADUATE_RESEARCH_ROOMS.CONTENT
                    .LABEL
                }
                ref={register()}
                defaultValue={field.content}
              />
            </Field>
            {index > 0 && (
              <RemoveResearchRoom
                onClick={() => remove(index)}
                data-testid="remove-research-room"
              >
                {MESSAGES.COMMON.BUTTON.REMOVE_FIELD}
              </RemoveResearchRoom>
            )}
          </Fields>
        </FieldsContainer>
      ))}

      {fields.length < MAX_NEW_GRADUATE_RESEARCH_ROOMS && (
        <AddField
          style={{ marginTop: '-8px' }}
          onClick={() => append({})}
          data-testid="add-research-room"
        />
      )}
    </Wrapper>
  );
};

export default NewGraduateResearchRoomsFormField;
